import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase'; // Use the functions instance with region from firebase.js
import { auth } from '../firebase';
import { 
  getInternalIntegrations, 
  updateIntegrationStatus, 
  getIntegrationStatus, 
  disconnectIntegration,
  getAccountIntegrations,
  disconnectAccountIntegration
} from './firestoreService';

// CRITICAL: This file uses Firebase callable functions ONLY
// If you see CORS errors, the browser is using OLD CACHED CODE
// Solution: Hard refresh (Ctrl+Shift+R) or clear browser cache
console.log('✅ integrationService.js loaded - using httpsCallable (NOT fetch)');

// Integration configuration
const INTEGRATION_CONFIG = {
  twilio: {
    name: 'Twilio',
    authUrl: 'https://auth.twilio.com/oauth/authorize',
    clientId: process.env.REACT_APP_TWILIO_CLIENT_ID,
    scope: 'chat:write,channels:read',
    redirectUri: `${window.location.origin}/auth/twilio/callback`
  },

  gdrive: {
    name: 'Google Drive',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/drive',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  quickbooks: {
    name: 'QuickBooks',
    authUrl: 'https://appcenter.intuit.com/connect/oauth2',
    clientId: process.env.REACT_APP_QUICKBOOKS_CLIENT_ID,
    scope: 'com.intuit.quickbooks.accounting',
    // QuickBooks requires exact redirect URI match - use production URL for both dev and prod
    // This must match exactly what's configured in QuickBooks Developer Console
    redirectUri: process.env.REACT_APP_QUICKBOOKS_REDIRECT_URI || 'https://portal.buildersbreakthrough.com/auth/quickbooks/callback'
  },
  zapier: {
    name: 'Zapier',
    authUrl: 'https://zapier.com/app/oauth/authorize',
    clientId: process.env.REACT_APP_ZAPIER_CLIENT_ID,
    scope: 'read',
    redirectUri: `${window.location.origin}/auth/zapier/callback`
  },
  googleanalytics: {
    name: 'Google Analytics',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/userinfo.email',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  stripe: {
    name: 'Stripe',
    authUrl: 'https://connect.stripe.com/oauth/authorize',
    clientId: process.env.REACT_APP_STRIPE_CLIENT_ID,
    scope: 'read_write',
    redirectUri: `${window.location.origin}/auth/stripe/callback`
  },
  meta: {
    name: 'Meta',
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
    clientId: process.env.REACT_APP_META_CLIENT_ID,
    scope: 'ads_management,ads_read',
    redirectUri: `${window.location.origin}/auth/meta/callback`
  },
  docusign: {
    name: 'DocuSign',
    authUrl: process.env.REACT_APP_DOCUSIGN_DEMO === 'true'
      ? 'https://account-d.docusign.com/oauth/auth'
      : 'https://account.docusign.com/oauth/auth',
    clientId: process.env.REACT_APP_DOCUSIGN_CLIENT_ID,
    scope: 'signature',
    // Use REACT_APP_DOCUSIGN_REDIRECT_URI if set (must match DocuSign app's registered URI exactly).
    // Falls back to the current origin, which requires localhost to be registered in the DocuSign app.
    redirectUri: process.env.REACT_APP_DOCUSIGN_REDIRECT_URI || `${window.location.origin}/auth/docusign/callback`
  },
  youtube: {
    name: 'YouTube',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/youtube',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  activecampaign: {
    name: 'ActiveCampaign',
    authUrl: 'https://api.activecampaign.com/oauth/authorize',
    clientId: process.env.REACT_APP_ACTIVECAMPAIGN_CLIENT_ID,
    scope: 'contacts:read contacts:write',
    redirectUri: `${window.location.origin}/auth/activecampaign/callback`
  },
  googlemaps: {
    name: 'Google Maps API',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/maps-platform',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  googleCalendar: {
    name: 'Google Calendar',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  gmail: {
    name: 'Gmail',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
    redirectUri: `${window.location.origin}/auth/google/callback`
  },
  calendly: {
    name: 'Calendly',
    authUrl: 'https://auth.calendly.com/oauth/authorize',
    clientId: process.env.REACT_APP_CALENDLY_CLIENT_ID,
    scope: 'default',
    redirectUri: `${window.location.origin}/auth/calendly/callback`
  },
  fathom: {
    name: 'Fathom',
    authUrl: 'https://app.fathom.video/oauth/authorize',
    clientId: process.env.REACT_APP_FATHOM_CLIENT_ID,
    scope: 'meetings:read meetings:write',
    redirectUri: `${window.location.origin}/auth/fathom/callback`
  }
};

export const initiateIntegrationConnection = (integrationKey) => {
  const config = INTEGRATION_CONFIG[integrationKey];
  
  if (!config) {
    throw new Error(`Unknown integration: ${integrationKey}`);
  }

  if (!config.clientId) {
    throw new Error(`Client ID not configured for ${integrationKey}`);
  }

  // Build OAuth URL
  // Encode redirect URI in state so token exchange uses the exact same URI (DocuSign, QuickBooks)
  let stateValue = integrationKey;
  if (integrationKey === 'quickbooks' || integrationKey === 'docusign') {
    stateValue = `${integrationKey}|${encodeURIComponent(config.redirectUri)}`;
  }
  
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    response_type: 'code',
    state: stateValue
  });

  // Google OAuth integrations need offline access for refresh tokens
  if (['googleCalendar', 'gmail', 'googleanalytics', 'gdrive', 'youtube', 'googlemaps'].includes(integrationKey)) {
    params.append('access_type', 'offline');
    params.append('prompt', 'consent');
  }

  const authUrl = `${config.authUrl}?${params.toString()}`;
  
  // Redirect to OAuth provider
  window.location.href = authUrl;
};

/**
 * Initiate an account-level OAuth connection for portal clients.
 * Encodes accountId in the state param so the callback can save tokens under
 * internalIntegrations/{accountId} instead of dotware.
 *
 * State format: "integrationKey|accountId|encodedRedirectUri"
 */
export const initiateAccountIntegrationConnection = (integrationKey, accountId) => {
  const config = INTEGRATION_CONFIG[integrationKey];

  if (!config) throw new Error(`Unknown integration: ${integrationKey}`);
  if (!config.clientId) throw new Error(`Client ID not configured for ${integrationKey}`);
  if (!accountId) throw new Error('accountId is required for account-level connections');

  const stateValue = `${integrationKey}|${accountId}|${encodeURIComponent(config.redirectUri)}`;

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    response_type: 'code',
    state: stateValue,
    access_type: 'offline',
    prompt: 'consent',
  });

  window.location.href = `${config.authUrl}?${params.toString()}`;
};

// Client helper for calling the integration callback callable function
// IMPORTANT: This uses Firebase callable functions (httpsCallable), NOT direct HTTP calls
// If you see CORS errors, the browser is using cached old code. Hard refresh (Ctrl+Shift+R)
export const handleIntegrationCallbackClient = async ({ code, state, accountId, realmId = null, redirectUri = null }) => {
  console.log('🔵 handleIntegrationCallbackClient called - using httpsCallable (NOT fetch)');
  console.log('🔵 Parameters:', {
    code: code ? code.substring(0, 10) + '...' : 'none',
    state,
    accountId: accountId || 'none',
    realmId: realmId || 'none',
    redirectUri: redirectUri || 'none'
  });
  
  // CRITICAL: If you see any fetch() calls to cloudfunctions.net URLs, that's OLD CACHED CODE
  // This function ONLY uses httpsCallable, never fetch()
  
  // Verify user is authenticated before making the call
  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('❌ User not authenticated - cannot call handleIntegrationCallback');
    throw new Error('User must be authenticated to complete integration');
  }
  
  console.log('✅ User authenticated:', currentUser.uid);
  
  try {
    // Ensure we have a fresh auth token (this ensures the SDK includes it in the request)
    const token = await currentUser.getIdToken(true); // Force refresh
    console.log('✅ Auth token obtained:', token ? 'present' : 'missing');
    console.log('✅ Functions instance region:', functions._delegate?._customDomain || functions._delegate?._url || 'us-central1');
    
    // Create the callable function reference
    // The SDK should automatically use the callable protocol, not HTTP
    const handleCallback = httpsCallable(functions, 'handleIntegrationCallback', {
      timeout: 60000 // 60 second timeout for OAuth operations
    });
    console.log('✅ httpsCallable created - this is the CORRECT way to call the function');
    console.log('✅ Callable function will use Firebase callable protocol (not direct HTTP)');
    
    const requestData = {
      code,
      state,
      accountId
    };
    
    // Add realmId for QuickBooks if provided
    if (realmId) {
      requestData.realmId = realmId;
    }
    
    // Add redirectUri if provided (critical for QuickBooks to match exactly)
    if (redirectUri) {
      requestData.redirectUri = redirectUri;
    }
    
    console.log('📤 Calling callable function with data:', {
      ...requestData,
      code: requestData.code ? requestData.code.substring(0, 10) + '...' : 'none',
      redirectUri: requestData.redirectUri || 'NOT PROVIDED'
    });
    
    const result = await handleCallback(requestData);
    console.log('✅ Callable function returned:', result.data);
    return result.data;
  } catch (error) {
    console.error('❌ Error in handleIntegrationCallbackClient:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error details:', error.details);
    
    // Provide more helpful error messages
    if (error.code === 'functions/unauthenticated' || error.code === 'unauthenticated') {
      throw new Error('Authentication required. Please log in and try again.');
    } else if (error.code === 'functions/internal' || error.code === 'internal') {
      const errorMsg = error.message || error.details?.originalError || 'An internal error occurred';
      throw new Error(`Integration failed: ${errorMsg}`);
    }
    
    throw error;
  }
};

// Legacy wrapper for backward compatibility
export const handleIntegrationCallback = async (integrationKey, code, state, realmId = null, accountId = null) => {
  return handleIntegrationCallbackClient({ code, state, accountId, realmId });
};

export const getIntegrationsStatus = async (options = {}) => {
  try {
    const integrations = await getInternalIntegrations(options);
    return integrations.connectedIntegrations || {};
  } catch (error) {
    console.error('Error getting integrations status:', error);
    throw error;
  }
};

export const getSingleIntegrationStatus = async (integrationKey) => {
  try {
    return await getIntegrationStatus(integrationKey);
  } catch (error) {
    console.error('Error getting integration status:', error);
    throw error;
  }
};

export const disconnectIntegrationConnection = async (integrationKey) => {
  try {
    await disconnectIntegration(integrationKey);
    return true;
  } catch (error) {
    console.error('Error disconnecting integration:', error);
    throw error;
  }
};

export const updateIntegrationConnection = async (integrationKey, status, authData = {}) => {
  try {
    await updateIntegrationStatus(integrationKey, status, authData);
    return true;
  } catch (error) {
    console.error('Error updating integration status:', error);
    throw error;
  }
};

// Helper function to get integration display name
export const getIntegrationDisplayName = (integrationKey) => {
  return INTEGRATION_CONFIG[integrationKey]?.name || integrationKey;
};

// Helper function to check if integration is configured
export const isIntegrationConfigured = (integrationKey) => {
  return !!INTEGRATION_CONFIG[integrationKey]?.clientId;
};

// Account-level integration (for portal clients)
export const getAccountIntegrationsStatus = async (accountId, options = {}) => {
  try {
    const data = await getAccountIntegrations(accountId, options);
    return data.connectedIntegrations || {};
  } catch (error) {
    console.error('Error getting account integrations status:', error);
    throw error;
  }
};

export const disconnectAccountIntegrationConnection = async (accountId, integrationKey) => {
  try {
    await disconnectAccountIntegration(accountId, integrationKey);
    return true;
  } catch (error) {
    console.error('Error disconnecting account integration:', error);
    throw error;
  }
}; 